import React, { useEffect, useState } from "react";
import { fetchRoomsReport, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { printTableReport } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// Front Desk — Slice 4: owner rooms report. Aggregated bookings, occupancy and
// guest-origin over Today / last 7 days / custom range.
const S = {
  en: {
    title: "Rooms report", subtitle: "Bookings, occupancy and where guests come from",
    today: "Today", last7: "Last 7 days", last30: "Last 30 days", custom: "Custom",
    from: "From", to: "To", apply: "Apply",
    bookings: "Rooms booked", room_nights: "Room-nights", occupancy: "Occupancy", revenue: "Room revenue",
    of_rooms: "{n} rooms over {d} days", origins: "Where guests come from", sources: "Booking source",
    none: "No bookings in this range.", src_walk_in: "Walk-in", src_phone: "Phone", src_corporate: "Corporate", src_ota: "OTA",
    unknown: "Unknown", print: "Print", metric: "Metric", value: "Value", count: "Count",
  },
  am: {
    title: "የክፍሎች ሪፖርት", subtitle: "ማስያዣዎች፣ ተሞልቶነት እና እንግዶች ከየት እንደመጡ",
    today: "ዛሬ", last7: "ያለፉ 7 ቀናት", last30: "ያለፉ 30 ቀናት", custom: "ብጁ",
    from: "ከ", to: "እስከ", apply: "ተግብር",
    bookings: "የተያዙ ክፍሎች", room_nights: "የክፍል ሌሊቶች", occupancy: "ተሞልቶነት", revenue: "የክፍል ገቢ",
    of_rooms: "{n} ክፍሎች በ{d} ቀናት", origins: "እንግዶች ከየት ይመጣሉ", sources: "የማስያዣ ምንጭ",
    none: "በዚህ ክልል ውስጥ ማስያዣ የለም።", src_walk_in: "በአካል የመጣ", src_phone: "ስልክ", src_corporate: "ድርጅት", src_ota: "OTA",
    unknown: "ያልታወቀ", print: "አትም", metric: "መለኪያ", value: "ዋጋ", count: "ብዛት",
  },
  om: {
    title: "Gabaasa kutaalee", subtitle: "Qabannoo, guutuu fi keessummoonni eessaa akka dhufan",
    today: "Har'a", last7: "Guyyoota 7 darban", last30: "Guyyoota 30 darban", custom: "Kan filatame",
    from: "Irraa", to: "Hamma", apply: "Raawwadhu",
    bookings: "Kutaalee qabaman", room_nights: "Halkanoota kutaa", occupancy: "Guutuu", revenue: "Galii kutaa",
    of_rooms: "kutaalee {n} guyyoota {d} keessatti", origins: "Keessummoonni eessaa dhufu", sources: "Madda qabannoo",
    none: "Hangii kana keessatti qabannoon hin jiru.", src_walk_in: "Kallattiin", src_phone: "Bilbila", src_corporate: "Dhaabbata", src_ota: "OTA",
    unknown: "Hin beekamne", print: "Maxxansi", metric: "Safartuu", value: "Gatii", count: "Baay'ina",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const iso = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().slice(0, 10); };

export default function RoomsReportPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [preset, setPreset] = useState("last7");
  const [range, setRange] = useState({ from: iso(-6), to: iso(0) });
  const [data, setData] = useState(null);

  const load = (from, to) => { setData(null); fetchRoomsReport(from, to).then(setData).catch(() => setData(null)); };
  useEffect(() => { load(range.from, range.to); /* eslint-disable-next-line */ }, [range.from, range.to]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "today") setRange({ from: iso(0), to: iso(0) });
    else if (p === "last7") setRange({ from: iso(-6), to: iso(0) });
    else if (p === "last30") setRange({ from: iso(-29), to: iso(0) });
  };

  const srcLabel = (k) => t("src_" + k) !== ("src_" + k) ? t("src_" + k) : k;
  const originLabel = (k) => (k === "Unknown" ? t("unknown") : k);

  const doPrint = () => {
    if (!data) return;
    const kpi = {
      heading: t("title"),
      headers: [t("metric"), t("value")],
      rows: [
        [t("bookings"), data.bookings],
        [t("room_nights"), data.room_nights],
        [t("occupancy"), data.occupancy_pct == null ? "—" : `${data.occupancy_pct}%`],
        [t("revenue"), formatPrice(data.revenue, cur)],
        ["ADR", data.adr == null ? "—" : formatPrice(data.adr, cur)],
        ["RevPAR", data.revpar == null ? "—" : formatPrice(data.revpar, cur)],
      ],
    };
    const sections = [kpi];
    if ((data.origins || []).length) sections.push({ heading: t("origins"), headers: [t("origins"), t("count")], rows: (data.origins || []).map((x) => [originLabel(x.label), x.count]) });
    if ((data.sources || []).length) sections.push({ heading: t("sources"), headers: [t("sources"), t("count")], rows: (data.sources || []).map((x) => [srcLabel(x.label), x.count]) });
    printTableReport({ title: t("title"), subtitle: `${t("from")} ${range.from} — ${t("to")} ${range.to}`, sections });
  };

  const presetBtn = (p, label) => (
    <button onClick={() => applyPreset(p)} style={{
      fontSize: 13, padding: "6px 14px", borderRadius: 20, cursor: "pointer",
      border: preset === p ? "1.5px solid #0F6E56" : "1px solid #e2e6ea",
      background: preset === p ? "#E1F5EE" : "#fff", color: preset === p ? "#0F6E56" : "#5b6472",
      fontWeight: preset === p ? 700 : 500,
    }}>{label}</button>
  );

  const Bars = ({ items }) => {
    const max = Math.max(1, ...items.map((x) => x.count));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((x, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 130, fontSize: 13, color: "#3a4652", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.label}</div>
            <div style={{ flex: 1, background: "#eef2f6", borderRadius: 6, height: 18, overflow: "hidden" }}>
              <div style={{ width: `${(x.count / max) * 100}%`, background: "#0F6E56", height: "100%", borderRadius: 6 }} />
            </div>
            <div style={{ width: 30, textAlign: "right", fontSize: 13, fontWeight: 600 }}>{x.count}</div>
          </div>
        ))}
      </div>
    );
  };

  const Metric = ({ label, value, sub, tint, accent }) => (
    <div style={{ background: tint, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 13, color: "#5f6b78" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#5f6b78", marginTop: 3 }}>{sub}</div>}
    </div>
  );

  const origins = (data && data.origins) || [];
  const sources = ((data && data.sources) || []).map((x) => ({ ...x, label: srcLabel(x.label) }));

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* range presets */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14, alignItems: "center" }}>
        {presetBtn("today", t("today"))}
        {presetBtn("last7", t("last7"))}
        {presetBtn("last30", t("last30"))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="date" value={range.from} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }} style={{ padding: 6, borderRadius: 8, border: "1px solid #d8dee2" }} />
          <span className="muted" style={{ fontSize: 13 }}>→</span>
          <input type="date" value={range.to} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }} style={{ padding: 6, borderRadius: 8, border: "1px solid #d8dee2" }} />
        </span>
        <span style={{ flex: 1 }} />
        <button className="btn secondary small" disabled={!data} onClick={doPrint}>🖨 {t("print")}</button>
      </div>

      {data == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : (
        <>
          {/* KPI cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 16 }}>
            <Metric label={t("bookings")} value={data.bookings} tint="#E6F1FB" accent="#185FA5" />
            <Metric label={t("room_nights")} value={data.room_nights} tint="#E1F5EE" accent="#0F6E56" />
            <Metric label={t("occupancy")} value={data.occupancy_pct == null ? "—" : `${data.occupancy_pct}%`} sub={t("of_rooms").replace("{n}", data.active_rooms).replace("{d}", data.days)} tint="#FAEEDA" accent="#854F0B" />
            <Metric label={t("revenue")} value={formatPrice(data.revenue, cur)} tint="#EAF3DE" accent="#27500A" />
            <Metric label="ADR" value={data.adr == null ? "—" : formatPrice(data.adr, cur)} tint="#EEEDFE" accent="#3C3489" />
            <Metric label="RevPAR" value={data.revpar == null ? "—" : formatPrice(data.revpar, cur)} tint="#FAECE7" accent="#712B13" />
          </div>

          {data.bookings === 0 ? (
            <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("none")}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginTop: 16 }}>
              <div style={{ ...CARD, padding: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("origins")}</div>
                <Bars items={origins.map((x) => ({ ...x, label: originLabel(x.label) }))} />
              </div>
              <div style={{ ...CARD, padding: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("sources")}</div>
                <Bars items={sources} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
